import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SITE } from "../../data/site";
import { useT } from "@/i18n/I18nContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactForm() {
  const { t } = useT();
  const interests = t("contact.interests");
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
    interest: interests[0],
  });
  const [status, setStatus] = useState("idle");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || form.message.length < 10) {
      toast.error(t("contact.validation_toast"));
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      toast.success(t("contact.success_toast"));
      setForm({
        name: "",
        email: "",
        company: "",
        subject: "",
        message: "",
        interest: interests[0],
      });
    } catch (err) {
      console.error(err);
      setStatus("error");
      toast.error(t("contact.error_toast"));
    } finally {
      setTimeout(() => setStatus("idle"), 2400);
    }
  }

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="tt-section relative"
      style={{ background: "var(--tt-surface)" }}
    >
      <div className="mx-auto max-w-[1480px] px-6 md:px-12">
        {/* Editorial heading above the form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mb-16"
        >
          <p className="tt-overline">{t("contact.overline")}</p>
          <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-[1.02] text-[var(--tt-text)]">
            {t("contact_extra.title_l")}{" "}
            <em className="italic text-[var(--tt-gold)]">{t("contact_extra.title_r")}</em>.
          </h2>
          <p className="mt-6 text-[var(--tt-text-dim)] font-light leading-relaxed max-w-2xl text-base md:text-lg">
            {t("contact_extra.sub")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <h3 className="font-serif text-3xl md:text-4xl leading-[1.05] text-[var(--tt-text)]">
            {t("contact.heading_1")}{" "}
            <em className="italic text-[var(--tt-gold)]">{t("contact.heading_2")}</em>?
          </h3>
          <p className="mt-5 text-[var(--tt-text-dim)] font-light leading-relaxed max-w-md">
            {t("contact.sub")}
          </p>

          <div className="mt-12 space-y-6">
            <div>
              <p className="tt-overline">{t("contact.email_label")}</p>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-link"
                className="mt-2 inline-block font-serif text-2xl md:text-3xl text-[var(--tt-text)] tt-link-underline"
              >
                {SITE.email}
              </a>
            </div>
            <div>
              <p className="tt-overline">{t("contact.location_label")}</p>
              <p className="mt-2 font-serif text-xl text-[var(--tt-text-dim)]">{SITE.city}</p>
            </div>
            <div>
              <p className="tt-overline">{t("contact.time_label")}</p>
              <p className="mt-2 font-serif text-xl text-[var(--tt-text-dim)]">{t("contact.time_value")}</p>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
            <Field label={t("contact.labels.name")} required>
              <input
                data-testid="contact-input-name"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder={t("contact.placeholders.name")}
                className="tt-input"
                required
              />
            </Field>
            <Field label={t("contact.labels.email")} required>
              <input
                data-testid="contact-input-email"
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder={t("contact.placeholders.email")}
                className="tt-input"
                required
              />
            </Field>
            <Field label={t("contact.labels.company")}>
              <input
                data-testid="contact-input-company"
                name="company"
                value={form.company}
                onChange={onChange}
                placeholder={t("contact.placeholders.company")}
                className="tt-input"
              />
            </Field>
            <Field label={t("contact.labels.type")}>
              <select
                data-testid="contact-select-interest"
                name="interest"
                value={form.interest}
                onChange={onChange}
                className="tt-input bg-transparent appearance-none cursor-pointer"
                style={{ colorScheme: "dark" }}
              >
                {interests.map((i) => (
                  <option key={i} value={i} className="bg-[#0a0f0d] text-[var(--tt-text)]">
                    {i}
                  </option>
                ))}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label={t("contact.labels.subject")} required>
                <input
                  data-testid="contact-input-subject"
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  placeholder={t("contact.placeholders.subject")}
                  className="tt-input"
                  required
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label={t("contact.labels.message")} required>
                <textarea
                  data-testid="contact-input-message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder={t("contact.placeholders.message")}
                  className="tt-input"
                  rows={5}
                  minLength={10}
                  required
                />
              </Field>
            </div>
          </div>

          <div className="mt-12 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-[var(--tt-text-muted)] text-xs font-light max-w-md">
              {t("contact.privacy")}
            </p>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={status === "loading"}
              className="tt-btn tt-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "loading" && <Loader2 size={14} className="animate-spin" />}
              {status === "success" && <Check size={14} />}
              {(status === "idle" || status === "error") && <Send size={14} />}
              {status === "loading"
                ? t("contact.cta_loading")
                : status === "success"
                ? t("contact.cta_success")
                : t("contact.cta_idle")}
            </button>
          </div>
        </motion.form>
        </div>

        {/* Average response time badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 pt-10 border-t border-[var(--tt-border)] flex flex-col md:flex-row md:items-center md:justify-between gap-6"
        >
          <div className="flex items-center gap-4">
            <span
              className="w-2 h-2 rounded-full"
              style={{
                background: "var(--tt-gold)",
                boxShadow: "0 0 18px var(--tt-gold)",
                animation: "tt-pulse 2.4s ease-in-out infinite",
              }}
            />
            <div>
              <p className="tt-overline">{t("contact_extra.response_label")}</p>
              <p className="mt-1 font-serif italic text-2xl md:text-3xl text-[var(--tt-text)]">
                {t("contact_extra.response_value")}
              </p>
            </div>
          </div>
          <a
            href={`mailto:${SITE.email}`}
            data-testid="contact-alt-cta"
            className="tt-btn"
          >
            {t("contact_extra.cta_alt")}
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="tt-overline flex items-center gap-2">
        {label}
        {required && <span className="text-[var(--tt-gold)]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
